import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StreetlightEfficiencyDialogComponent } from '@app/energy-map/dialog/streetlight-efficiency-dialog/streetlight-efficiency-dialog.component';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { EnergyMapService } from '@app/energy-map/services/energy-map-service';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { SidebarComponent } from '@app/shared/components/sidebar/sidebar.component';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ApplicationInsightEvent, PermissionCode } from '@app/shared/enums';
import { MunicipalityRestriction, MunicipalityRestrictionsService } from '@app/shared/services/api/municipality-restrictions.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService, CurrentUser } from '@app/shared/services/auth.service';
import { SidebarService } from '@app/shared/services/sidebar.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { isEmpty, isNil, uniq } from 'lodash';
import { Subject, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { LoadScenarioDialogComponent } from '../../dialog/load-scenario-dialog/load-scenario-dialog.component';
import { StreetLightPlannerSidebarNewScenarioComponent } from '../street-light-planner-sidebar-new-scenario/street-light-planner-sidebar-new-scenario.component';

@Component({
  selector: 'app-street-light-planner-sidebar-scenario',
  templateUrl: './street-light-planner-sidebar-scenario.component.html',
  styleUrls: ['./street-light-planner-sidebar-scenario.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class StreetLightPlannerSidebarScenarioComponent extends SidebarComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public newScenarioDisabled = false;
  public scenario = '';
  public selectedToggleOption = 'search';
  public uniqMunicipalities: MapStoreMunicipality[] = [];
  user: CurrentUser;
  private municipalities: MapStoreMunicipality[] = [];
  private selectedMunicipality: MapStoreMunicipality | null = null;
  private streetlightEfficiencyDialogOpen = false;

  constructor(
    public authService: AuthService,
    public sidebarService: SidebarService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private energyMapService: EnergyMapService,
    private mapStoreService: MapStoreService,
    private matDialog: MatDialog,
    private municipalityRestrictionService: MunicipalityRestrictionsService,
    private userMunicipalitiesService: UserMunicipalitiesService
  ) {
    super(sidebarService);
    this.user = this.authService.currentUser;
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerAccess);
  }

  public ngOnInit(): void {
    this.userMunicipalitiesService.municipalities$.pipe(takeUntil(this.unsubscribe$)).subscribe((municipalities: MapStoreMunicipality[]) => {
      this.municipalities = municipalities || [];
      this.uniqMunicipalities = uniq(
        this.municipalities
          .map((municipality) => municipality)
          .sort((a: MapStoreMunicipality, b: MapStoreMunicipality) => {
            return a.name?.localeCompare(b.name);
          })
      );
    });

    this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged(),
        switchMap((municipality: MapStoreMunicipality) => {
          this.selectedMunicipality = municipality;
          return this.municipalityRestrictionService
            .getByPermissionCode(this.selectedMunicipality.guid, PermissionCode.STREETLIGHT_PLANNER)
            .pipe(catchError(() => of([] as MunicipalityRestriction[])));
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((restrictions: MunicipalityRestriction[]) => {
        this.newScenarioDisabled = (isNil(this.mapStoreService.lampMarkers) && isEmpty(this.mapStoreService.lampMarkers)) || !isEmpty(restrictions);
      });

    this.toggleStreetlightEfficiencyDialog();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.streetlightEfficiencyDialogOpen && this.scenario !== 'open') {
      this.matDialog.closeAll();
    }
  }

  public select(scenario: string): void {
    this.scenario = scenario;
  }

  public open(): void {
    if (this.scenario === 'new') {
      this.sidebarService.showComponent(StreetLightPlannerSidebarNewScenarioComponent);
    }

    if (this.scenario === 'open') {
      this.openLoadScenarioModal();
      this.close();
    }
  }

  private openLoadScenarioModal(): void {
    this.matDialog.closeAll();
    this.matDialog
      .open(LoadScenarioDialogComponent, {
        panelClass: ['-sm'],
      })
      .afterClosed()
      .subscribe((a: { displayNoResult: boolean } | undefined) => {
        if (a?.displayNoResult) {
          this.displayNoScenarioModal();
        }
      });
  }

  private displayNoScenarioModal(): void {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: 'Aktuell ist kein gespeichertes Szenario vorhanden.',
        orientation: 'row',
        alignment: 'start',
      },
      panelClass: ['-md'],
    });
  }

  public triggerClose(): void {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.streetlightPlannerCancel);
    this.matDialog.closeAll();
    this.close();
  }

  private formatName(municipality: MapStoreMunicipality): string {
    if (!this.authService.isMunicipalityWorker) {
      return `${municipality.name} (${municipality.key})`;
    } else {
      return `${municipality.name}`;
    }
  }

  public getSelectedMunicipalityText(): string {
    return this.formatName(this.selectedMunicipality as MapStoreMunicipality);
  }

  public setMunicipality(municipality: MapStoreMunicipality): void {
    this.newScenarioDisabled = false;
    this.selectedMunicipality = this.municipalities.filter((value: MapStoreMunicipality) => {
      this.energyMapService.municipalityChanged(true);
      return value.guid === municipality.guid;
    })[0];
    this.userMunicipalitiesService.updateMunicipality(this.selectedMunicipality);
  }

  public toggleStreetlightEfficiencyDialog(): void {
    if (this.authService.permissions.STREETLIGHT_EFFICIENCY) {
      const dialogRef = this.matDialog.openDialogs.filter((a) => a.id === 'streetlightEfficiencyDialog')[0];
      if (dialogRef) {
        dialogRef.close();
        this.streetlightEfficiencyDialogOpen = false;
      } else {
        this.matDialog.open(StreetlightEfficiencyDialogComponent, {
          data: {
            isPlanner: false,
          },
          width: '1000px',
          hasBackdrop: false,
          disableClose: true,
          panelClass: ['-md', 'streetlight-efficiency-dialog'],
          id: 'streetlightEfficiencyDialog',
        });
        this.streetlightEfficiencyDialogOpen = true;
      }
    }
  }
}
